import { PitchNote } from "./pitch-detect";

const notes = {
  C0: 16.35,
  "C#0": 17.32,
  D0: 18.35,
  "D#0": 19.45,
  E0: 20.6,
  F0: 21.83,
  "F#0": 23.12,
  G0: 24.5,
  "G#0": 25.96,
  A0: 27.5,
  "A#0": 29.14,
  B0: 30.87,
  C1: 32.7,
  "C#1": 34.65,
  D1: 36.71,
  "D#1": 38.89,
  E1: 41.2,
  F1: 43.65,
  "F#1": 46.25,
  G1: 49.0,
  "G#1": 51.91,
  A1: 55.0,
  "A#1": 58.27,
  B1: 61.74,
  C2: 65.41,
  "C#2": 69.3,
  D2: 73.42,
  "D#2": 77.78,
  E2: 82.41,
  F2: 87.31,
  "F#2": 92.5,
  G2: 98.0,
  "G#2": 103.83,
  A2: 110.0,
  "A#2": 116.54,
  B2: 123.47,
  C3: 130.81,
  "C#3": 138.59,
  D3: 146.83,
  "D#3": 155.56,
  E3: 164.81,
  F3: 174.61,
  "F#3": 185.0,
  G3: 196.0,
  "G#3": 207.65,
  A3: 220.0,
  "A#3": 233.08,
  B3: 246.94,
  C4: 261.63,
  "C#4": 277.18,
  D4: 293.66,
  "D#4": 311.13,
  E4: 329.63,
  F4: 349.23,
  "F#4": 369.99,
  G4: 392.0,
  "G#4": 415.3,
  A4: 440.0,
  "A#4": 466.16,
  B4: 493.88,
  C5: 523.25,
  "C#5": 554.37,
  D5: 587.33,
  "D#5": 622.25,
  E5: 659.25,
  F5: 698.46,
  "F#5": 739.99,
  G5: 783.99,
  "G#5": 830.61,
  A5: 880.0,
  "A#5": 932.33,
  B5: 987.77,
  C6: 1046.5,
  "C#6": 1108.73,
  D6: 1174.66,
  "D#6": 1244.51,
  E6: 1318.51,
  F6: 1396.91,
  "F#6": 1479.98,
  G6: 1567.98,
  "G#6": 1661.22,
  A6: 1760.0,
  "A#6": 1864.66,
  B6: 1975.53,
  C7: 2093.0,
  "C#7": 2217.46,
  D7: 2349.32,
  "D#7": 2489.02,
  E7: 2637.02,
  F7: 2793.83,
  "F#7": 2959.96,
  G7: 3135.96,
  "G#7": 3322.44,
  A7: 3520.0,
  "A#7": 3729.31,
  B7: 3951.07,
  C8: 4186.01,
  "C#8": 4434.92,
  D8: 4698.63,
  "D#8": 4978.03,
  E8: 5274.04,
  F8: 5587.65,
  "F#8": 5919.91,
  G8: 6271.93,
  "G#8": 6644.88,
  A8: 7040.0,
  "A#8": 7458.62,
  B8: 7902.13,
} as const;

type Key = keyof typeof notes;
export type Note = {
  note: string;
  frequency: number;
  freqFrom: number;
  freqTo: number;
};

const noteFreqs: Note[] = Object.entries(notes)
  .sort((a, b) => a[1] - b[1])
  .map(([note, frequency], i, all) => {
    const prevFreq = all[i - 1]?.[1] ?? all[0][1];
    const nextFreq = all[i + 1]?.[1] ?? all[all.length - 1][1];
    // Logarithmic middle point
    const freqFrom = Math.sqrt(prevFreq * frequency);
    const freqTo = Math.sqrt(frequency * nextFreq);
    return { note, frequency, freqFrom, freqTo };
  });

const A4_FREQUENCY = 440;
const A4_MIDI_NOTE = 69;
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const frequencyToMidiNote = (freq: number): number =>
  A4_MIDI_NOTE + 12 * Math.log2(freq / A4_FREQUENCY);

const midiNoteToFrequency = (midiNote: number): number =>
  A4_FREQUENCY * Math.pow(2, (midiNote - A4_MIDI_NOTE) / 12);

const midiNoteToNoteName = (midiNote: number): string => {
  const octave = Math.floor(midiNote / 12) - 1;
  const noteName = NOTE_NAMES[midiNote % 12];
  return `${noteName}${octave}`;
};

const calculateFrequencyBoundaries = (noteFrequency: number, midiNote: number) => {
  const lowerNoteFreq = midiNoteToFrequency(midiNote - 1);
  const upperNoteFreq = midiNoteToFrequency(midiNote + 1);
  return {
    freqFrom: Math.sqrt(lowerNoteFreq * noteFrequency),
    freqTo: Math.sqrt(noteFrequency * upperNoteFreq)
  };
};

export const freqToNote = (freq: number): Note => {
  if (freq <= 0) {
    throw new Error("Frequency must be positive");
  }

  const midiNote = frequencyToMidiNote(freq);
  const roundedMidiNote = Math.round(midiNote);
  const noteFrequency = midiNoteToFrequency(roundedMidiNote);
  const { freqFrom, freqTo } = calculateFrequencyBoundaries(noteFrequency, roundedMidiNote);

  return {
    note: midiNoteToNoteName(roundedMidiNote),
    frequency: noteFrequency,
    freqFrom,
    freqTo
  };
};

export const noteToFreq = (note: Key): number => notes[note];

export const notesBetween = (from: Key, to: Key) => {
  const fromIndex = noteFreqs.findIndex(({ note }) => note === from);
  const toIndex = noteFreqs.findIndex(({ note }) => note === to);
  return noteFreqs.slice(fromIndex, toIndex + 1);
};

export type NoteData = {
  note: Note;
  data: Float32Array;
  startTime: number;
  endTime: number;
};

export function splitToNotes(
  pitchData: PitchNote[],
  buffer: AudioBuffer,
  audioBuffLength: number,
  quantization: number,
  tempo: number
): NoteData[] {
  const rawAudioData = buffer.getChannelData(0);
  let notes: NoteData[] = [];
  const chunkSize = Math.round(
    (buffer.sampleRate * 60) / (quantization * tempo)
  );
  const numChunks = Math.floor(audioBuffLength / chunkSize);
  //if (numChunks !== pitchData.length) throw new Error("Universe did not align"); // Can't remember how the stars needed to align for this

  let blockStartIndex = 0;
  for (let i = 0; i < pitchData.length - 1; i++) {
    const curr = pitchData[i];
    const next = pitchData[i + 1];
    const currNote = freqToNote(curr.frequency);
    if (
      next.frequency <= currNote.freqTo &&
      next.frequency >= currNote.freqFrom
    )
      continue;

    const startBlock = pitchData[blockStartIndex];
    const chunk = rawAudioData.slice(
      blockStartIndex * chunkSize,
      (i + 1) * chunkSize
    );

    notes.push({
      note: currNote,
      data: chunk,
      startTime: startBlock.startTime,
      endTime: curr.endTime,
    });
    blockStartIndex = i + 1;
  }

  return notes;
}

export function noteDifferenceInSemotones(a: Note, b: Note) {
  return Math.abs(noteFreqs.indexOf(a) - noteFreqs.indexOf(b));
}
